import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Share2, MoreVertical, Settings, Trash2 } from "lucide-react";
import type { Database } from "@/types/database.types";
import { supabase } from "@/lib/supabaseClient";
import { useState } from "react";
import { useProjects } from "@/contexts/ProjectsContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type MemberProjectRow = {
  role: string;
  user_id: string;
  username: string;
  full_name?: string | null;
  avatar_url?: string | null;
};

type ProjectData = Database["public"]["Functions"]["get_user_projects"]["Returns"][number] & {
  members: MemberProjectRow[];
};

interface ProjectCardProps {
  project: ProjectData;
}

export function ProjectCard({ project }: ProjectCardProps) {
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { refreshProjects } = useProjects();
  const { user } = useAuth();

  const progressPercentage =
    project.total_tasks_count > 0
      ? Math.round((project.completed_tasks_count / project.total_tasks_count) * 100)
      : 0;

  // Parse members JSON
  const members: MemberProjectRow[] = project.members || [];

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Determine progress bar color based on completion percentage
  const getProgressColor = () => {
    if (progressPercentage >= 80) return "bg-green-500";
    if (progressPercentage >= 50) return "bg-blue-500";
    if (progressPercentage >= 25) return "bg-orange-500";
    return "bg-red-500";
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSharing(true);

    try {
      if (!user) {
        alert("You must be logged in");
        return;
      }

      // Create an invite link
      const token = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

      const { error: inviteError } = await supabase.from("project_invites").insert({
        project_id: project.project_id,
        inviter_id: user.id,
        token,
        role: "viewer",
        expires_at: expiresAt.toISOString(),
      });

      if (inviteError) {
        throw inviteError;
      }

      const inviteUrl = `${window.location.origin}/invite/${token}`;
      await navigator.clipboard.writeText(inviteUrl);
      alert("Invite link copied to clipboard!");
    } catch (error) {
      console.error("Error creating invite:", error);
      alert("Failed to create invite link. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        `Are you sure you want to delete "${project.project_title}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    setDeleting(true);

    try {
      const { error } = await supabase.from("projects").delete().eq("id", project.project_id);

      if (error) {
        throw error;
      }

      // Refresh projects list
      await refreshProjects();

      // Navigate away if we're on the project page
      navigate("/projects");
    } catch (error) {
      console.error("Error deleting project:", error);
      alert("Failed to delete project. Please try again.");
      setDeleting(false);
    }
  };

  const handleSettings = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/project/${project.project_id}/settings`);
  };

  return (
    <Card className="p-4 md:p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="flex-1 min-w-0">
          <Link to={`/project/${project.project_id}`}>
            <h3 className="text-lg md:text-xl font-bold mb-2 hover:text-primary transition-colors">
              {project.project_title}
            </h3>
          </Link>
        </div>
        <div
          className="flex items-center gap-1 md:gap-2 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outline"
            size="sm"
            className="gap-1 md:gap-2"
            onClick={handleShare}
            disabled={sharing}
          >
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">{sharing ? "Sharing..." : "Share"}</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleSettings}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleDelete}
                className="text-destructive focus:text-destructive"
                disabled={deleting}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {deleting ? "Deleting..." : "Delete"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        {project.closest_task_title && (
          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
            {project.closest_task_title}
          </p>
        )}
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-primary/20 mb-2">
          <div
            className={`h-full transition-all ${getProgressColor()}`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <p className="text-sm text-muted-foreground">
          {project.completed_tasks_count}/{project.total_tasks_count} tasks complete
        </p>
      </div>

      {/* Members */}
      {members.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {members.slice(0, 5).map((member: MemberProjectRow, index: number) => (
              <Avatar key={member.user_id || index} className="border-2 border-background h-8 w-8">
                <AvatarImage
                  src={member.avatar_url || undefined}
                  alt={member.full_name || member.username || "User"}
                />
                <AvatarFallback className="text-xs">
                  {getInitials(member.full_name || member.username)}
                </AvatarFallback>
              </Avatar>
            ))}
          </div>
          {members.length > 5 && (
            <span className="text-xs text-muted-foreground ml-2">+{members.length - 5}</span>
          )}
        </div>
      )}
    </Card>
  );
}